import {
  HttpApiEndpoint,
  HttpApiGroup,
  HttpApiSchema,
  OpenApi,
} from 'effect/unstable/httpapi';
import { OptionalUserAuthMiddleware, UserAuthMiddleware } from './middleware';
import { Schema } from 'effect';
import {
  BadGatewayError,
  NotFoundError,
  TooManyRequestsError,
  InternalServerError,
} from './errors';
import { uploadableDocumentTypeFromMime } from './schemas/document-type';

// =============================================================================
// Importable-attachment public contract
// =============================================================================

/**
 * The document type of a Zotero attachment, or undefined when we cannot import
 * it. Derived from the uploadable document types; HTML is excluded because
 * Zotero stores web-page snapshots as ZIP archives, which `/items/:key/file`
 * returns instead of the page.
 */
export const zoteroAttachmentDocumentType = (contentType: string) => {
  const documentType = uploadableDocumentTypeFromMime(contentType);
  return documentType === 'html' ? undefined : documentType;
};

const isSupportedZoteroContentType = (contentType: string | null) =>
  contentType !== null &&
  zoteroAttachmentDocumentType(contentType) !== undefined;

// Attachment link modes whose bytes live on Zotero's servers and are therefore
// downloadable via `/items/:key/file`. The two "linked" modes are not: a
// `linked_url` is a web bookmark (no file), and a `linked_file` points at a
// file on the user's own computer that was never uploaded to Zotero — both
// return 404 from the file endpoint, which previously surfaced to the user as a
// failed import on an item the dialog had offered as importable.
export const DOWNLOADABLE_ZOTERO_LINK_MODES: ReadonlySet<string> = new Set([
  'imported_file',
  'imported_url',
]);

/**
 * Whether a Zotero attachment item is importable: an attachment (not a note),
 * stored on Zotero (a downloadable link mode, not a web bookmark or a link to a
 * local file), whose bytes are actually in Zotero's cloud storage, and a content
 * type our pipeline handles.
 *
 * `md5` is the cloud-storage signal: Zotero sets it (a content hash) only once
 * an attachment's file has been uploaded to its storage. A non-null `md5`
 * predicts a downloadable `/file` (302 → S3); an explicit `null` means the
 * metadata exists but the bytes were never synced — common on free accounts
 * over the 300 MB file-storage quota — and `/file` 404s, so we drop it.
 *
 * `undefined` (field absent) means the API predates this field — a deploy-skew
 * window where the FE has this filter but the deployed API isn't sending `md5`
 * yet. We allow it through rather than hiding every attachment, falling back to
 * today's behavior (offer it, surface a clean `DocumentInaccessibleError` if the
 * download 404s). The download-time error also remains the backstop for
 * WebDAV-synced files, which can carry an `md5` Zotero itself can't serve.
 */
export function isImportableZoteroAttachment(item: {
  item_type: string;
  link_mode: string | null;
  content_type: string | null;
  md5?: string | null | undefined;
}): boolean {
  return (
    item.item_type === 'attachment' &&
    item.link_mode !== null &&
    DOWNLOADABLE_ZOTERO_LINK_MODES.has(item.link_mode) &&
    item.md5 !== null &&
    isSupportedZoteroContentType(item.content_type)
  );
}

// Why a reference item can't be imported, for an inline UI explanation.
// `no_file` — no downloadable supported file is attached (only notes, web
// bookmarks/linked files, or an attachment whose bytes were never synced).
// `unsupported_type` — there is a downloadable file, but not a format we read.
export type ZoteroImportability =
  | { readonly importable: true }
  | {
      readonly importable: false;
      readonly reason: 'no_file' | 'unsupported_type';
    };

/**
 * Resolve, from the listing alone, whether an item can be imported — and if not,
 * why — so the dialog can disable the row with a reason instead of letting the
 * user discover the failure on click.
 *
 * For a reference item this leans on `best_attachment`, derived from Zotero's
 * own `links.attachment`: Zotero only emits that link when the item has a
 * downloadable "best attachment" (bytes in cloud storage), and it carries that
 * attachment's content type. Absent ⇒ nothing downloadable; present ⇒ check the
 * type. `best_attachment === undefined` is the deploy-skew fallback (API predates
 * the field): assume an item with children is importable, as the dialog did
 * before, rather than disabling everything.
 */
export function zoteroItemImportability(item: {
  item_type: string;
  link_mode: string | null;
  content_type: string | null;
  md5?: string | null | undefined;
  num_children: number;
  best_attachment?:
    | {
        content_type: string | null;
        size: number | null;
      }
    | null
    | undefined;
}): ZoteroImportability {
  if (item.item_type === 'attachment') {
    if (isImportableZoteroAttachment(item)) return { importable: true };
    // Distinguish "wrong format" from "no usable bytes" for the message.
    const hasBytes =
      item.link_mode !== null &&
      DOWNLOADABLE_ZOTERO_LINK_MODES.has(item.link_mode) &&
      item.md5 !== null;
    return {
      importable: false,
      reason: hasBytes ? 'unsupported_type' : 'no_file',
    };
  }

  // Reference item.
  if (item.best_attachment === undefined) {
    return item.num_children > 0
      ? { importable: true }
      : { importable: false, reason: 'no_file' };
  }
  if (item.best_attachment === null) {
    return { importable: false, reason: 'no_file' };
  }
  return isSupportedZoteroContentType(item.best_attachment.content_type)
    ? { importable: true }
    : { importable: false, reason: 'unsupported_type' };
}

// =============================================================================
// Response schemas
// =============================================================================
//
// The browse endpoints mirror Zotero's Web API 1:1 — each call maps to one
// upstream request. This avoids the fan-out / streaming-cursor complexity of an
// orchestrated "deep listing" endpoint and aligns the UX with how the official
// Zotero client behaves (lazy tree, scoped items list, search at root).

const ZoteroStatusResponse = Schema.Union([
  Schema.Struct({
    connected: Schema.Literal(false),
  }),
  Schema.Struct({
    connected: Schema.Literal(true),
    display_name: Schema.NullOr(Schema.String),
    provider_user_id: Schema.String,
    connected_at: Schema.String,
  }),
]);

export const ZoteroCollectionItem = Schema.Struct({
  key: Schema.String,
  name: Schema.String,
  parent_collection: Schema.NullOr(Schema.String),
  num_items: Schema.Int,
  // Direct subcollection count. The UI uses this to decide whether to render
  // a chevron without speculatively fetching `/collections/:key/collections`.
  num_collections: Schema.Int,
});
export type ZoteroCollectionItem = typeof ZoteroCollectionItem.Type;

const ZoteroCollectionsResponse = Schema.Struct({
  collections: Schema.Array(ZoteroCollectionItem),
  total_results: Schema.Int,
  // Offset for the next page, or null when the full set has been read.
  next_start: Schema.NullOr(Schema.Int),
});

const ZoteroCreator = Schema.Struct({
  name: Schema.String,
});

export const ZoteroItemSummary = Schema.Struct({
  key: Schema.String,
  item_type: Schema.String,
  title: Schema.String,
  // Only present on attachment-type items. Useful for rendering a filename
  // subtitle and for the import path to construct attachment payloads without
  // a second fetch when the selected item is itself a standalone attachment.
  filename: Schema.NullOr(Schema.String),
  // Present on attachment-type items. Combined with `link_mode` it lets the
  // import path skip linked-URL "attachments" and unsupported types without
  // another round trip.
  content_type: Schema.NullOr(Schema.String),
  link_mode: Schema.NullOr(Schema.String),
  // Content hash of the stored file; non-null only once the attachment's bytes
  // are uploaded to Zotero's cloud storage. The import filter uses it to avoid
  // offering attachments whose files were never synced (they 404 on download).
  // Optional so a client still decodes responses from an API deployed before
  // this field existed (treated as "unknown" — see isImportableZoteroAttachment).
  md5: Schema.optional(Schema.NullOr(Schema.String)),
  // Derived from Zotero's `links.attachment`: the item's downloadable "best
  // attachment" (bytes in cloud storage) and its content type, or null when the
  // item has no downloadable file. Lets the dialog mark importability up front
  // without a per-item children fetch. Optional for the same deploy-skew reason.
  best_attachment: Schema.optional(
    Schema.NullOr(
      Schema.Struct({
        content_type: Schema.NullOr(Schema.String),
        size: Schema.NullOr(Schema.Int),
      }),
    ),
  ),
  creators: Schema.Array(ZoteroCreator),
  date: Schema.NullOr(Schema.String),
  num_children: Schema.Int,
});
export type ZoteroItemSummary = typeof ZoteroItemSummary.Type;

const ZoteroItemsResponse = Schema.Struct({
  items: Schema.Array(ZoteroItemSummary),
  total_results: Schema.Int,
  // Offset for the next page, or null if this was the last one. Computed
  // against Zotero's raw (pre-filter) result set so pagination stays correct
  // even when the server filters out notes/childless references.
  next_start: Schema.NullOr(Schema.Int),
});

const ZoteroChildrenResponse = Schema.Struct({
  children: Schema.Array(ZoteroItemSummary),
});

// Quick-search mode. titleCreatorYear (Zotero's default) searches titles +
// creators + year; everything also searches full-text content.
const ZoteroSearchMode = Schema.Literals(['titleCreatorYear', 'everything']);

const ZoteroConnectTicketResponse = Schema.Struct({
  // Short-lived signed ticket the mobile app passes to /connect?ticket=… .
  ticket: Schema.String,
});

// =============================================================================
// Endpoints
// =============================================================================

const statusEndpoint = HttpApiEndpoint.get('GetZoteroStatus', '/status', {
  success: ZoteroStatusResponse.pipe(HttpApiSchema.status(200)),
  error: InternalServerError,
})
  .middleware(UserAuthMiddleware)
  .annotate(OpenApi.Summary, 'Get Zotero connection status');

// Mints a short-lived signed ticket that identifies the connecting user. The
// mobile app calls this (with its session cookie) and hands the ticket to
// /connect in the system browser, which carries no app session cookie. Web
// omits it entirely (identified by its same-origin cookie).
const connectTicketEndpoint = HttpApiEndpoint.post(
  'CreateZoteroConnectTicket',
  '/connect-ticket',
  {
    success: ZoteroConnectTicketResponse.pipe(HttpApiSchema.status(201)),
    error: InternalServerError,
  },
)
  .middleware(UserAuthMiddleware)
  .annotate(OpenApi.Summary, 'Mint a short-lived Zotero connect ticket')
  .annotate(
    OpenApi.Description,
    'Returns a short-lived signed ticket the mobile app passes to /connect (in the system browser, which lacks the app session cookie) to identify the connecting user.',
  );

// Connect + callback are browser-navigation endpoints that return 302 redirects
// and set cookies. They use the typed contract, but handlers return
// HttpServerResponse directly (which HttpApiEndpoint supports).
// Auth uses OptionalUserAuthMiddleware because browser GET doesn't send the
// Authorization header — session is resolved from cookies inside the middleware.
const connectEndpoint = HttpApiEndpoint.get('ConnectZotero', '/connect', {
  query: Schema.Struct({
    // Mobile (system browser) passes a short-lived signed ticket from
    // /connect-ticket to identify the user; web omits it and is identified by
    // its session cookie. Ticket presence also marks the flow as mobile so the
    // callback redirects to the app's `<scheme>://zotero-callback` deep link,
    // where the scheme is this stage's `mobileDeepLinkSchemeConfig`.
    ticket: Schema.optional(Schema.String),
  }),
  success: HttpApiSchema.Empty(302),
  error: InternalServerError,
})
  .middleware(OptionalUserAuthMiddleware)
  .annotate(OpenApi.Summary, 'Initiate Zotero OAuth connection')
  .annotate(
    OpenApi.Description,
    'Redirects the user to Zotero to authorize the integration',
  );

const callbackEndpoint = HttpApiEndpoint.get('ZoteroCallback', '/callback', {
  query: Schema.Struct({
    oauth_token: Schema.optional(Schema.String),
    oauth_verifier: Schema.optional(Schema.String),
    // Set by the proxy flow: if present, this callback host is acting as a
    // relay and should forward the OAuth params to `return_to`.
    return_to: Schema.optional(Schema.String),
  }),
  success: HttpApiSchema.Empty(302),
  error: InternalServerError,
})
  .middleware(OptionalUserAuthMiddleware)
  .annotate(OpenApi.Summary, 'Zotero OAuth callback')
  .annotate(
    OpenApi.Description,
    'Target of the Zotero OAuth redirect; exchanges the token and redirects to settings',
  );

const disconnectEndpoint = HttpApiEndpoint.delete(
  'DisconnectZotero',
  '/disconnect',
  {
    success: Schema.Void.pipe(HttpApiSchema.status(204)),
    error: InternalServerError,
  },
)
  .middleware(UserAuthMiddleware)
  .annotate(OpenApi.Summary, 'Disconnect Zotero integration');

const collectionsEndpoint = HttpApiEndpoint.get(
  'ListZoteroCollections',
  '/collections',
  {
    query: Schema.Struct({
      start: Schema.optional(Schema.FiniteFromString),
      limit: Schema.optional(Schema.FiniteFromString),
    }),
    success: ZoteroCollectionsResponse.pipe(HttpApiSchema.status(200)),
    error: [
      NotFoundError,
      TooManyRequestsError,
      BadGatewayError,
      InternalServerError,
    ],
  },
)
  .middleware(UserAuthMiddleware)
  .annotate(OpenApi.Summary, "List a page of the user's Zotero collections")
  .annotate(
    OpenApi.Description,
    "Mirrors Zotero's /collections — one paginated page of the full flat collection set, each item carrying its parent's key. The browse dialog loops this endpoint (matching the official Zotero web-library) to assemble the tree client-side, so expand/collapse is instant after first load. Each call is 1:1 with a single Zotero request.",
  );

const topItemsEndpoint = HttpApiEndpoint.get(
  'ListZoteroTopItems',
  '/items/top',
  {
    query: Schema.Struct({
      start: Schema.optional(Schema.FiniteFromString),
      limit: Schema.optional(Schema.FiniteFromString),
      q: Schema.optional(Schema.String),
      qmode: Schema.optional(ZoteroSearchMode),
    }),
    success: ZoteroItemsResponse.pipe(HttpApiSchema.status(200)),
    error: [
      NotFoundError,
      TooManyRequestsError,
      BadGatewayError,
      InternalServerError,
    ],
  },
)
  .middleware(UserAuthMiddleware)
  .annotate(OpenApi.Summary, 'List top-level Zotero items library-wide')
  .annotate(
    OpenApi.Description,
    "Mirrors Zotero's /items/top: every top-level item across the library, with optional server-side quick search (`q`/`qmode`). Items are filtered to those importable for audio conversion (no notes; supported standalone attachments only; childless references dropped).",
  );

const collectionTopItemsEndpoint = HttpApiEndpoint.get(
  'ListZoteroCollectionTopItems',
  '/collections/:collection_key/items/top',
  {
    params: Schema.Struct({ collection_key: Schema.String }),
    query: Schema.Struct({
      start: Schema.optional(Schema.FiniteFromString),
      limit: Schema.optional(Schema.FiniteFromString),
      q: Schema.optional(Schema.String),
      qmode: Schema.optional(ZoteroSearchMode),
    }),
    success: ZoteroItemsResponse.pipe(HttpApiSchema.status(200)),
    error: [
      NotFoundError,
      TooManyRequestsError,
      BadGatewayError,
      InternalServerError,
    ],
  },
)
  .middleware(UserAuthMiddleware)
  .annotate(OpenApi.Summary, 'List top-level items directly in a collection')
  .annotate(
    OpenApi.Description,
    "Mirrors Zotero's /collections/:key/items/top: items filed directly in the given collection only. Does NOT recurse into subcollections — that matches the canonical Zotero client UX (browse the tree to reach subfolder contents, or use library-wide search).",
  );

const itemChildrenEndpoint = HttpApiEndpoint.get(
  'ListZoteroItemChildren',
  '/items/:item_key/children',
  {
    params: Schema.Struct({ item_key: Schema.String }),
    success: ZoteroChildrenResponse.pipe(HttpApiSchema.status(200)),
    error: [
      NotFoundError,
      TooManyRequestsError,
      BadGatewayError,
      InternalServerError,
    ],
  },
)
  .middleware(UserAuthMiddleware)
  .annotate(OpenApi.Summary, 'List child items of a Zotero item')
  .annotate(
    OpenApi.Description,
    "Mirrors Zotero's /items/:key/children. Returns the raw child set (attachments + notes). The import path filters these client-side to supported attachments using `item_type`/`content_type`/`link_mode` on each child.",
  );

// =============================================================================
// Group
// =============================================================================

export const zoteroGroup = HttpApiGroup.make('Zotero')
  .add(statusEndpoint)
  .add(connectTicketEndpoint)
  .add(connectEndpoint)
  .add(callbackEndpoint)
  .add(disconnectEndpoint)
  .add(collectionsEndpoint)
  .add(topItemsEndpoint)
  .add(collectionTopItemsEndpoint)
  .add(itemChildrenEndpoint)
  .prefix('/v2/integrations/zotero');
